import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { funilApi, ApiError } from '@/api/funil';
import { Button } from '@/components/atoms';
import { useAuth } from '@/store/useAuth';

import styles from './Signup.module.css';

export interface SignupProps {
  initialEmpty?: boolean;
}

export function Signup({ initialEmpty = false }: SignupProps = {}) {
  const { t } = useTranslation(['funil', 'common']);
  const navigate = useNavigate();

  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [empty] = useState(initialEmpty);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(t('funil:signup.erro_senha_curta'));
      return;
    }

    setLoading(true);
    try {
      const res = await funilApi.signup({
        nome_empresa: nomeEmpresa.trim(),
        email: email.trim(),
        password,
        slug: slug.trim() || undefined,
      });

      useAuth
        .getState()
        .setAuth(res.access_token, res.username, res.role, res.must_change_password);

      navigate('/conectar-wordpress', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t('funil:signup.erro_409'));
      } else if (err instanceof ApiError && err.status === 429) {
        setError(t('funil:signup.erro_429'));
      } else {
        setError(t('funil:signup.erro_generico'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.root}>
      <form className={styles.card} onSubmit={handleSubmit} noValidate>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('funil:signup.titulo')}</h1>
          <p className={styles.subtitle}>{t('funil:signup.subtitulo')}</p>
        </div>

        {loading && <div className={styles.loading}>{t('funil:signup.carregando')}</div>}

        {error && <div className={styles.error}>{error}</div>}

        {empty && (
          <div className={styles.empty}>
            <p>{t('funil:signup.vazio_titulo')}</p>
            <p>{t('funil:signup.vazio_descricao')}</p>
          </div>
        )}

        <label className={styles.field}>
          <span>{t('funil:signup.empresa_rotulo')}</span>
          <input
            value={nomeEmpresa}
            onChange={(e) => setNomeEmpresa(e.target.value)}
            required
            autoFocus
          />
        </label>

        <label className={styles.field}>
          <span>{t('funil:signup.email_rotulo')}</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>

        <label className={styles.field}>
          <span>{t('funil:signup.senha_rotulo')}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        <label className={styles.field}>
          <span>{t('funil:signup.slug_rotulo')}</span>
          <input value={slug} onChange={(e) => setSlug(e.target.value)} />
          <p className={styles.help}>{t('funil:signup.slug_ajuda')}</p>
        </label>

        <Button type="submit" loading={loading} className={styles.submit}>
          {t('funil:signup.botao_enviar')}
        </Button>

        <div className={styles.footer}>
          <span>{t('funil:signup.ja_tem_conta')}</span>
          <Link to="/login" className={styles.link}>
            {t('funil:signup.fazer_login')}
          </Link>
        </div>
      </form>
    </div>
  );
}
